import { isPublicSafeUsername } from '../lib/wingo-username';

// Google Sheets API v4 service
// This replaces the deprecated Google Visualization API

const GOOGLE_SHEETS_API_KEY = 'AIzaSyBxGQoJz8QZQZQZQZQZQZQZQZQZQZQZQZQZQ'; // You'll need to replace this with a real API key
const SPREADSHEET_ID = '1eWkRJZph0X75PXfyWd22iKtgDsQhiv7RrH9X8xDKwu4';

interface SheetData {
  values: string[][];
}

interface SheetsApiResponse {
  valueRanges: Array<{
    range: string;
    majorDimension: string;
    values: string[][];
  }>;
}

export class GoogleSheetsApiService {
  private static instance: GoogleSheetsApiService;
  private apiKey: string;

  private constructor() {
    this.apiKey = GOOGLE_SHEETS_API_KEY;
  }

  public static getInstance(): GoogleSheetsApiService {
    if (!GoogleSheetsApiService.instance) {
      GoogleSheetsApiService.instance = new GoogleSheetsApiService();
    }
    return GoogleSheetsApiService.instance;
  }

  /**
   * Fetch data from Google Sheets using the CSV export method (fallback)
   * This is a simpler approach that doesn't require API keys
   */
  async fetchSheetAsCSV(sheetName: string): Promise<string[][]> {
    try {
      // Try multiple URL formats for better compatibility
      const urls = [
        // Format 1: Direct CSV export
        `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}/export?format=csv&gid=${this.getSheetGid(sheetName)}`,
        // Format 2: Google Visualization API
        `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(sheetName)}&t=${Date.now()}`,
        // Format 3: Alternative CSV export
        `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}/export?format=csv&sheet=${encodeURIComponent(sheetName)}`
      ];

      let lastError: Error | null = null;
      
      for (const url of urls) {
        try {
          console.log(`Trying URL for ${sheetName}:`, url);
          
          const response = await fetch(url, { 
            cache: 'no-store',
            headers: {
              'Accept': 'text/csv,text/plain,*/*',
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const csvText = await response.text();
          
          // Check if we got valid CSV data
          if (!csvText.trim() || csvText.includes('<!DOCTYPE html>')) {
            throw new Error('Invalid CSV response');
          }
          
          const rows = csvText.trim().split('\n');
          
          // Parse CSV rows with better handling of quoted values
          return rows.map(row => {
            const columns: string[] = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < row.length; i++) {
              const char = row[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                columns.push(current.trim().replace(/^"|"$/g, ''));
                current = '';
              } else {
                current += char;
              }
            }
            columns.push(current.trim().replace(/^"|"$/g, '')); // Add the last column
            
            return columns;
          });
        } catch (error) {
          console.log(`Failed with URL ${url}:`, error);
          lastError = error as Error;
          continue;
        }
      }
      
      throw lastError || new Error(`All URL formats failed for sheet ${sheetName}`);
    } catch (error) {
      console.error(`Error fetching sheet ${sheetName}:`, error);
      throw error;
    }
  }

  /**
   * Get the GID (Google ID) for a sheet name
   */
  private getSheetGid(sheetName: string): string {
    const gidMap: { [key: string]: string } = {
      'Sheet1': '1680833263',
      'Log': '270601813',
      'Summary': '482134402'
    };
    return gidMap[sheetName] || '0';
  }

  /**
   * Fetch data using Google Sheets API v4 (requires API key)
   */
  async fetchSheetData(sheetRanges: string[]): Promise<SheetsApiResponse> {
    try {
      const rangesParam = sheetRanges.map(range => encodeURIComponent(range)).join('&ranges=');
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values:batchGet?ranges=${rangesParam}&key=${this.apiKey}`;
      
      const response = await fetch(url, { 
        cache: 'no-store',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching sheet data via API v4:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard data
   */
  async getLeaderboardData(): Promise<any[]> {
    try {
      const rows = await this.fetchSheetAsCSV('Sheet1');
      if (rows.length < 2) return [];
      const header = rows[0].map((c) => c.trim());
      const wingleCol = header.indexOf('Wingle (L32)');
      const data = rows.slice(1);

      console.log('🔍 Raw usernames from Sheet1:', data.map(row => `"${row[0]?.trim() || ''}"`));

      const result = data
        .filter(row => row.length >= 5)
        .map((row) => {
          const username = row[0]?.trim() || '';
          const balance = parseFloat(row[1]) || 0;
          const mined = parseFloat(row[2]) || 0;
          const distance = parseFloat(row[3]) || 0;
          const lastMined = row[4]?.trim() || '';
          const votingShare = parseFloat(row[5]) || 0;
          const rank = parseInt(row[6]) || 0;
          const wingleRaw =
            wingleCol >= 0 ? row[wingleCol] : row.length >= 17 ? row[16] : row[14];
          const wingleL32 = parseFloat(String(wingleRaw ?? '').trim()) || 0;
          
          return {
            user: username,
            balance: balance,
            totalMined: mined,
            distance: distance,
            lastMined: lastMined,
            votingShare: votingShare,
            rank: rank,
            wingleL32: wingleL32
          };
        })
        .filter(entry => entry.user && isPublicSafeUsername(entry.user));
      
      // Debug: Log final usernames
      console.log('🔍 Final usernames in leaderboard:', result.map(entry => `"${entry.user}"`));
      
      return result;
    } catch (error) {
      console.error('Error getting leaderboard data:', error);
      return [];
    }
  }

  /**
   * Get log data
   */
  async getLogData(): Promise<any[]> {
    try {
      const rows = await this.fetchSheetAsCSV('Log');
      const data = rows.slice(1); // skip header row
      
      // Debug: Log all usernames from the log
      console.log('🔍 Raw usernames from Log sheet:', data.map(row => `"${row[1]?.trim() || ''}"`));
      
      const result = data
        .filter(row => row.length >= 13)
        .map((row) => {
          return {
            id: parseInt(row[0]) || 0,
            fullId: row[12]?.trim() || '',
            username: row[1]?.trim() || '',
            date: row[2]?.trim() || '',
            wingoMined: parseInt(row[3]) || 0,
            kmLogged: parseFloat(row[4]) || 0,
            initiation: row[7]?.trim() === 'Yes',
            category: row[10]?.trim() || '',
            activityLink: row[5]?.trim() || ''
          };
        })
        .filter(entry => entry.username && isPublicSafeUsername(entry.username));
      
      // Debug: Log final usernames from log
      console.log('🔍 Final usernames in log:', result.map(entry => `"${entry.username}"`));
      
      return result;
    } catch (error) {
      console.error('Error getting log data:', error);
      return [];
    }
  }

  /**
   * Get summary data (for last updated time)
   */
  async getSummaryData(): Promise<string> {
    try {
      const rows = await this.fetchSheetAsCSV('Summary');
      // Get the last updated time from F2 (row 1, column 5)
      const lastUpdatedCell = rows[1]?.[5]?.trim().replace(/^["']|["']$/g, '') || '';
      return lastUpdatedCell;
    } catch (error) {
      console.error('Error getting summary data:', error);
      return '';
    }
  }
}

export default GoogleSheetsApiService;
